import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Image as ImageIcon, Upload, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
  'wealth', 'love', 'family', 'sports', 'travel', 'nature',
  'performance', 'self', 'health', 'faith', 'luxury', 'pets', 'contribution', 'freedom'
];

const AdminImageManager = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('wealth');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState({ text: '', type: '' });

  const API = process.env.REACT_APP_BACKEND_URL;

  const loadImages = async (cat) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/vision-board/admin/images/${cat}`);
      if (res.ok) {
        const data = await res.json();
        setImages(data.images || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages(selectedCategory);
  }, [selectedCategory]);

  const handleAdd = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    setMessage({ text: '', type: '' });
    try {
      const res = await fetch(`${API}/api/vision-board/admin/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newUrl.trim(), category: selectedCategory }),
      });
      const data = await res.json();
      setMessage({ text: data.message, type: 'success' });
      setNewUrl('');
      loadImages(selectedCategory);
    } catch (e) {
      setMessage({ text: 'Failed to add image', type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Please select an image file', type: 'error' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ text: '', type: '' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', selectedCategory);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/vision-board/admin/images/upload`);

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };

      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      setMessage({ text: `Image uploaded to "${selectedCategory}"`, type: 'success' });
      loadImages(selectedCategory);
    } catch (err) {
      setMessage({ text: `Upload failed: ${err.message}`, type: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (url) => {
    try {
      const res = await fetch(
        `${API}/api/vision-board/admin/images?url=${encodeURIComponent(url)}&category=${selectedCategory}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setImages(prev => prev.filter(img => img.url !== url));
        setMessage({ text: 'Image deleted', type: 'success' });
      }
    } catch (e) {
      setMessage({ text: 'Failed to delete', type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white p-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition"
            data-testid="admin-back-btn"
          >
            <ArrowLeft size={20} className="text-white/50" />
          </button>
          <h1
            className="text-2xl font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Image Library
          </h1>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="category-tabs">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm capitalize transition-all ${
                selectedCategory === cat
                  ? 'bg-[#4A6741]/60 text-white border border-[#4A6741]/40'
                  : 'bg-white/5 text-white/50 hover:bg-white/10 border border-transparent'
              }`}
              data-testid={`cat-tab-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Add new image — URL or Upload */}
        <div className="rounded-xl p-5 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {/* Upload file */}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-testid="image-upload-input" />
          
          <motion.button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm mb-4"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: uploading ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, rgba(90,120,80,0.8), rgba(65,95,55,0.9))',
              border: '1px solid rgba(140,180,120,0.2)',
            }}
            whileHover={!uploading ? { scale: 1.01 } : {}}
            whileTap={!uploading ? { scale: 0.98 } : {}}
            data-testid="upload-image-btn"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="text-white/60 animate-spin" />
                <span className="text-white/60">Uploading... {uploadProgress}%</span>
              </>
            ) : (
              <>
                <Upload size={16} className="text-white/80" />
                <span className="text-white/80">Upload Image File</span>
              </>
            )}
          </motion.button>

          {uploading && (
            <div className="mb-4 h-1 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: 'rgba(90,120,80,0.8)' }}
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-white/8" />
            <span className="text-white/20 text-xs">or paste URL</span>
            <div className="flex-1 h-px bg-white/8" />
          </div>

          {/* URL input */}
          <div className="flex gap-3" data-testid="add-image-form">
            <input
              type="text"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="Paste image URL..."
              className="flex-1 px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4A6741]/50"
              data-testid="image-url-input"
            />
            <motion.button
              onClick={handleAdd}
              disabled={adding || !newUrl.trim()}
              className="px-5 py-3 rounded-lg bg-[#4A6741]/60 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-40"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="add-image-btn"
            >
              <Plus size={16} /> {adding ? 'Adding...' : 'Add'}
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {message.text && (
            <motion.p
              className={`text-sm mb-4 px-4 py-2 rounded-lg ${message.type === 'error' ? 'text-red-300/80 bg-red-500/10' : 'text-[#4A6741] bg-green-500/10'}`}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="admin-message"
            >
              {message.text}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Image grid */}
        <div className="mb-4 text-white/40 text-sm">
          {loading ? 'Loading...' : `${images.length} custom images in "${selectedCategory}"`}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="image-grid">
          {images.map((img, i) => (
            <div key={img.url + i} className="relative group aspect-square rounded-lg overflow-hidden bg-white/5">
              <img
                src={img.url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <button
                onClick={() => handleDelete(img.url)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-900/70 text-white/80 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-img-${i}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {images.length === 0 && !loading && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-white/20">
              <ImageIcon size={48} className="mb-3" />
              <p className="text-sm">No custom images yet</p>
              <p className="text-xs mt-1">Upload a file or paste a URL above</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminImageManager;
